import React, { useEffect } from 'react';
import Lottie from 'react-lottie';
import { useNavigate } from 'react-router-dom';
import printing from 'assets/lottie/printing';
import { FlexBox, Box, Typography, Button, Grid, MuiBox } from '@kis-technologies/kis-ui';
import { selectors as hardwareSelectors, actions as hardwareActions } from 'services/hardware';
import { selectors as kioskSelectors } from 'services/kiosk';
import { actions as ticketingActions, selectors as ticketingSelectors } from 'services/ticketing';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { ActionOverlay } from 'components/ActionOverlay';
import { useStyles } from './styled';

// Lottie Animation Config
const lottieOptions = {
  loop: true,
  autoplay: true,
  animationData: printing,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid meet'
  }
};

const PrintingView: React.FC = () => {
  const navigate = useNavigate();
  const printData = useSelector(hardwareSelectors.selectPrintData);
  const kioskLoading = useSelector(kioskSelectors.selectLoading);
  const deliveryStatus = useSelector(ticketingSelectors.selectDeliveryStatus);

  const { printAllJobs, resetDelivery } = useActions({
    printAllJobs: hardwareActions.printAllJobs,
    resetDelivery: ticketingActions.resetDelivery
  });

  const classes = useStyles();

  useEffect(() => {
    printAllJobs();
  }, []);

  const onBackToSetup = () => {
    resetDelivery();
    navigate('/systems');
  };

  return (
    <>
      <Grid className={classes.setupViewPrinting}>
        <Grid container className={classes.setupViewPrintingContainer}>
          <MuiBox className={classes.setupPrintingContentBox}>
            <Grid item lg={12} xl={6} className={classes.setupViewPrintingText}>
              {/* Show printing content until the delivery completes */}
              {deliveryStatus !== 'completed' ? (
                <Box width={425} flexOverride="none" margin="0 0 9">
                  <Typography className={classes.setupViewPrintingHeaders}>
                    Printing {(printData.count as number) + 1} of {printData.total}
                  </Typography>
                  <Typography fontSize={25}>Please look below for your tickets</Typography>
                </Box>
              ) : (
                <MuiBox className={classes.setupViewPrintingCompleteBox}>
                  <Typography className={classes.setupViewPrintingHeaders}>Printing Complete</Typography>
                  <MuiBox className={classes.setupPrintingSubHeader}>
                    <Typography fontSize={25}>Please look below for your tickets</Typography>
                  </MuiBox>
                  <Button
                    customSize="giant"
                    appearance="primary"
                    width={475}
                    onClick={onBackToSetup}
                    isLoading={kioskLoading}
                    data-testid="back-to-setup-button"
                    className={classes.backToSetupLandscape}
                  >
                    Back to Setup
                  </Button>
                </MuiBox>
              )}
            </Grid>
            <Grid item lg={12} xl={6} className={classes.SetupPrintingImageContainer}>
              <FlexBox fullHeight padding="0 20" align="center" justify="center">
                <Lottie options={lottieOptions} isClickToPauseDisabled />
              </FlexBox>
              {deliveryStatus === 'completed' && (
                <FlexBox align="center" justify="center">
                  <Button
                    customSize="giant"
                    appearance="primary"
                    width={475}
                    onClick={onBackToSetup}
                    isLoading={kioskLoading}
                    data-testid="back-to-setup-button"
                    className={classes.backToSetupPortrait}
                  >
                    Back to Setup
                  </Button>
                </FlexBox>
              )}
            </Grid>
          </MuiBox>
        </Grid>
      </Grid>

      {/* Action Overlay */}
      {deliveryStatus === 'failed' && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={800}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                Looks like something went wrong.
              </Typography>
            </Box>
          }
          description="Looks like something went wrong while printing."
          cancelText="Go Back"
          onCancel={onBackToSetup}
        />
      )}
    </>
  );
};

export default PrintingView;
